import type { Claim, DriftIssue } from "../../types.js";
import {
  resolveDualRootPath,
  isPathGitignored,
  _clearTsConfigCache,
  _clearFileIndexCache,
  _clearGitignoreCache,
} from "./_resolve-path.js";

// Re-export so callers can `import { isPathGitignored } from "./checkers/path.js"`.
export { isPathGitignored };

const PLACEHOLDER_WORDS = /(?:^|[/_-])(?:new|example|your|sample|my|foo|bar|placeholder|template)(?:[/_.-]|$)/i;

/**
 * Scoped npm package pattern: `@scope/package` or `@scope/package/...`
 * These are NOT file paths and should be skipped entirely.
 */
const SCOPED_NPM_RE = /^@[a-z0-9][\w.-]*\/[a-z0-9][\w.-]*(?:\/|$)/i;

/** Check that all claimed paths exist on disk */
export function checkPaths(
  claims: Claim[],
  projectRoot: string,
  scaffoldRoot: string
): DriftIssue[] {
  const issues: DriftIssue[] = [];
  const pathClaims = claims.filter(
    (c) => c.kind === "path" && !c.negated
  );

  for (const claim of pathClaims) {
    // Skip scoped npm packages — they are dependencies, not file paths
    if (SCOPED_NPM_RE.test(claim.value)) continue;

    if (pathExists(claim.value, projectRoot, scaffoldRoot)) continue;

    // Downgrade to warning when confidence is low:
    // - pattern files, placeholder words, example-intent claims
    // - bare filenames without slashes (often named as supported formats, not repo-specific claims)
    // - medium-confidence claims (less certain the file should exist here)
    // - gitignored paths: intentionally not committed, scaffold is correct to mention them
    const isPattern = claim.source.includes("patterns/");
    const isPlaceholder = PLACEHOLDER_WORDS.test(claim.value);
    const isExample = claim.intent === "example" || claim.confidence === "low";
    const isBareFilename = !claim.value.includes("/");
    const isMediumConfidence = claim.confidence === "medium";
    const isGitignore = isPathGitignored(claim.value, projectRoot);
    const severity = isPattern || isPlaceholder || isExample || isBareFilename || isMediumConfidence || isGitignore ? "warning" : "error";

    issues.push({
      code: "MISSING_PATH",
      severity,
      file: claim.source,
      line: claim.line,
      message: `Referenced path does not exist: ${claim.value}`,
      claim,
      suggestion: isExample
        ? `Replace the illustrative path \`${claim.value}\` with a real repo path or mark it more explicitly as an example.`
        : isGitignore
          ? `\`${claim.value}\` matches a .gitignore rule — verify the file exists locally (it is intentionally not committed).`
          : `Update this scaffold reference to an existing path, or create the missing file at \`${claim.value}\` if it should exist.`,
    });
  }

  return issues;
}

const pathExistsCache = new Map<string, boolean>();

function pathExists(value: string, projectRoot: string, scaffoldRoot: string): boolean {
  const cacheKey = `${projectRoot}:${value}`;
  const cached = pathExistsCache.get(cacheKey);
  if (cached !== undefined) return cached;
  const result = resolveDualRootPath(value, { projectRoot, scaffoldRoot }, {
    tryStripScaffoldPrefix: true,
    tryTsConfigAliases: true,
    tryFuzzyMatch: true,
  });
  pathExistsCache.set(cacheKey, result);
  return result;
}

// Re-export test helpers from _resolve-path.ts so existing test imports keep working.
export { _clearTsConfigCache, _clearFileIndexCache, _clearGitignoreCache };
